import React from 'react';
import PropTypes from 'prop-types';
import { Plus } from 'lucide-react';
import KitButton from '../../components/ui/Button';
import { controlResetClassName, cx } from '../../components/ui/classNames';
import EventCardAnchor from './EventCard';
import { decadeGroups, foundGroups } from './exploreColumns';

const CHIP_CLASS_NAME = 'group inline-flex h-7 max-w-[260px] items-center gap-0.5 rounded-full border border-solid py-0 pl-2.5 pr-1 text-[12px] font-medium';
// Filled while in the table, outlined on the bench, in each kind's colour:
// the accent for standard events, amber for found periods and the theme's
// violet for your events.
const CHIP_TONES = Object.freeze({
    standard: { table: 'border-tbl-accent bg-ui-selected text-ui-ink', bench: 'border-ui-line-strong bg-transparent text-ui-ink-soft' },
    found: { table: 'border-ui-warning-line bg-ui-warning-bg text-ui-warning', bench: 'border-ui-warning-line bg-transparent text-ui-warning' },
    yours: { table: 'border-ui-event-line bg-ui-event-bg text-ui-event', bench: 'border-ui-event-line bg-transparent text-ui-event' },
});
const CHIP_BUTTON_CLASS_NAME = cx(
    controlResetClassName,
    'min-w-0 cursor-pointer truncate border-0 bg-transparent p-0 text-inherit',
    'focus:outline-none focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-ui-focus',
);
// Hidden until the chip is pointed at or focused, except on phones, which have no hover.
const CHIP_INFO_CLASS_NAME = 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100';

function EventChip({ column, inTable, baselineLabel, onToggle, onEventAction }) {
    return (
        <EventCardAnchor column={column} baselineLabel={baselineLabel} onAction={onEventAction}
            className={cx(CHIP_CLASS_NAME, CHIP_TONES[column.tag][inTable ? 'table' : 'bench'])} infoClassName={CHIP_INFO_CLASS_NAME}>
            <button type="button" className={CHIP_BUTTON_CLASS_NAME} aria-pressed={inTable} onClick={() => onToggle(column.id)}>
                {column.chipName || column.name}
            </button>
        </EventCardAnchor>
    );
}

EventChip.propTypes = {
    column: PropTypes.shape({
        id: PropTypes.string.isRequired, tag: PropTypes.oneOf(Object.keys(CHIP_TONES)).isRequired,
        name: PropTypes.string.isRequired, chipName: PropTypes.string,
    }).isRequired,
    inTable: PropTypes.bool.isRequired,
    baselineLabel: PropTypes.string.isRequired,
    onToggle: PropTypes.func.isRequired,
    onEventAction: PropTypes.func,
};

// One box of chips with its own Add all and Remove all, in rows by decade,
// or for the found periods by side.
function EventBox({ title, columns, inTable, note = null, groupBy = decadeGroups, extra = null, onAddAll, onRemoveAll, ...chipProps }) {
    const benched = columns.filter((column) => !inTable.has(column.id)).map((column) => column.id);
    const shown = columns.filter((column) => inTable.has(column.id)).map((column) => column.id);
    const groups = groupBy(columns);
    return (
        <section aria-label={title} className="grid min-w-0 content-start gap-1.5 rounded-lg border border-solid border-ui-line p-2">
            <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
                <h4 className="m-0 mr-auto text-[11px] font-bold uppercase tracking-normal text-ui-muted">{title}</h4>
                {extra}
                <KitButton size="sm" className="px-1 py-0.5" disabled={!benched.length} onClick={() => onAddAll(benched)}>Add all</KitButton>
                <KitButton size="sm" className="px-1 py-0.5" disabled={!shown.length} onClick={() => onRemoveAll(shown)}>Remove all</KitButton>
            </div>
            {note && <p className="m-0 text-[11.5px] text-content-secondary">{note}</p>}
            {columns.length > 0 && groups.map((group) => (
                <div key={group.label} role="group" aria-label={`${title}, ${group.label}`}
                    className="flex min-w-0 flex-wrap items-center gap-1">
                    <span aria-hidden="true" className="w-11 shrink-0 text-[10.5px] font-semibold text-ui-muted">{group.label}</span>
                    {group.columns.map((column) => (
                        <EventChip key={column.id} column={column} inTable={inTable.has(column.id)} {...chipProps} />
                    ))}
                </div>
            ))}
        </section>
    );
}

EventBox.propTypes = {
    title: PropTypes.string.isRequired,
    columns: PropTypes.array.isRequired,
    inTable: PropTypes.instanceOf(Set).isRequired,
    note: PropTypes.string,
    groupBy: PropTypes.func,
    extra: PropTypes.node,
    onAddAll: PropTypes.func.isRequired,
    onRemoveAll: PropTypes.func.isRequired,
};

/**
 * The events the grid can show, above it: the published standard library
 * and your own events in two boxes, and the baseline's worst and best
 * periods found by the scans in a small third box. Each event is a chip
 * with its title: filled while its column is in the table, outlined on the
 * bench, and a click moves it from one to the other. Its card has the
 * dates, the description or your notes, what happened in the market then,
 * and its actions (`onEventAction`). `messages` stand in for a box's chips
 * while its read loads or fails; `onNewEvent(anchor)` opens the New event
 * pop-out, whose button `newEventRef` receives.
 */
export default function EventsPanel({
    catalogue, inTable, baselineLabel, messages = {}, canCreate = true, newEventRef, onToggle, onAddAll, onRemoveAll, onNewEvent, onEventAction,
}) {
    const chipProps = { inTable, baselineLabel, onToggle, onAddAll, onRemoveAll, onEventAction };
    return (
        <div className="grid min-w-0 gap-1.5">
            <div className="grid min-w-0 gap-1.5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                <EventBox title="Standard library" columns={catalogue.standard} note={messages.standard} {...chipProps} />
                <EventBox title="Your events" columns={catalogue.yours} note={messages.yours} {...chipProps}
                    extra={(
                        <KitButton ref={newEventRef} size="sm" className="gap-0.5 px-1 py-0.5" disabled={!canCreate}
                            onClick={(event) => onNewEvent(event.currentTarget)}>
                            <Plus aria-hidden="true" size={13} strokeWidth={2.5} />
                            New event
                        </KitButton>
                    )} />
            </div>
            <EventBox title={`Found for ${baselineLabel}`} columns={catalogue.found} note={messages.found} groupBy={foundGroups} {...chipProps} />
        </div>
    );
}

EventsPanel.propTypes = {
    catalogue: PropTypes.shape({ standard: PropTypes.array, yours: PropTypes.array, found: PropTypes.array }).isRequired,
    inTable: PropTypes.instanceOf(Set).isRequired,
    baselineLabel: PropTypes.string.isRequired,
    messages: PropTypes.shape({ standard: PropTypes.string, yours: PropTypes.string, found: PropTypes.string }),
    canCreate: PropTypes.bool,
    newEventRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
    onToggle: PropTypes.func.isRequired,
    onAddAll: PropTypes.func.isRequired,
    onRemoveAll: PropTypes.func.isRequired,
    onNewEvent: PropTypes.func.isRequired,
    onEventAction: PropTypes.func,
};
